import { trackEvent } from 'helpers/analytics';
import { middlewareTrack, titaniteView } from 'apis/amethyst';
import { sendMonetateEvent } from 'apis/monetate';

const SEARCH_PAGE_VIEW = 'SEARCH_PAGE_VIEW';

const mapSortType = value => {
  switch (value) {
    case 'bestForYou/desc':
      return 'BEST_FOR_YOU';
    case 'relevance/desc':
      return 'RELEVANCE';
    case 'isNew/desc/goLiveDate/desc/recentSalesStyle/desc':
      return 'NEW_ARRIVALS';
    case 'productRating/desc':
      return 'CUSTOMER_RATING';
    case 'recentSalesStyle/desc':
      return 'BEST_SELLERS';
    case 'price/asc':
      return 'PRICE_LOW_TO_HIGH';
    case 'price/desc':
      return 'PRICE_HIGH_TO_LOW';
    case 'brandNameFacetLC/asc/productName/asc':
      return 'BRAND_SORT';
    case 'percentOff/desc':
      return 'PERCENT_OFF';
    case 'onSale/desc':
      return 'ON_SALE';
    case 'trending/desc':
      return 'TRENDING';
    default:
      return 'UNKNOWN_SORT';
  }
};

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SearchPageView.proto
  *
  * Note: reco impressions are sent separately with the recommendationWrapper
  *
  * @param {integer} itemsFound - # of items in results
  * @param {string} searchTerm - Search term duh
  * @param {object} selected - Object of selected filters from Redux state
  * @param {object} sort - Object of sort from Redux state
*/
export const pvSearch = ({ products: { totalProductCount }, filters: { originalTerm, selected, sort } }) => {
  titaniteView();

  const autoFacets = [];
  const { singleSelects, multiSelects } = selected;

  const addSelectedFacets = selects => {
    Object.keys(selects).forEach(attribute => {
      selects[attribute].forEach(value => {
        autoFacets.push({ attribute, value });
      });
    });
  };

  if (singleSelects) {
    addSelectedFacets(singleSelects);
  }
  if (multiSelects) {
    addSelectedFacets(multiSelects);
  }

  let currentSort = 'relevance/desc';
  const sortKeys = Object.keys(sort);
  if (sortKeys.length > 0) {
    currentSort = Object.keys(sort).map(v => `${v}/${sort[v]}`).join('/');
  }

  const defaultSort = mapSortType(currentSort);
  middlewareTrack({
    searchPageView: {
      autoFacets,
      itemsFound: totalProductCount,
      searchTerm: originalTerm,
      defaultSort
    }
  });
};

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/FacetClick.proto
  *
  * @param {string} facetGroup - Attribute of facet
  * @param {string} facetName - Name of facet
  * @param {object} crumb - Clean crumb object containing the facet name & value in the object
  * @param {object} breadcrumbRemove - A little dirty object for the name & value of the facet. Need
  * to clean the removeName so it sends a sensible attribute property.
  * @param {bool} selected - true if selected
  * @param {bool} deselected - true if deselected
*/
export const evFacetClick = ({ facetGroup, facetName, crumb, breadcrumbRemove, facetClickSource, selected, deselected }) => {
  let attribute, value;

  if (facetGroup) {
    attribute = facetGroup;
  } else if (crumb) {
    attribute = crumb.name;
  } else if (breadcrumbRemove) {
    // sometimes we get something like: 'Remove brandFacet: Ugg' and we need it to be 'brandFacet'
    attribute = breadcrumbRemove.removeName.replace(/:.*|Remove /g,'');
  }

  if (facetName) {
    value = facetName;
  } else if (crumb) {
    ({ value } = crumb);
  } else if (breadcrumbRemove) {
    value = breadcrumbRemove.name;
  }

  return {
    facetClick: {
      facetClick: {
        attribute,
        value
      },
      selected,
      deselected,
      facetClickSource
    }
  };
};

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SortSearchClick.proto
  *
  * @param {string} sortType - See WebsiteEnums.proto file for potential values
  * Sort Map used to find value based off of values in WebsiteEnums file
*/
const SortTypeMap = {
  'unknown': 'UNKNOWN_SORT',
  'relevance': 'RELEVANCE',
  'new arrivals': 'NEW_ARRIVALS',
  'customer rating': 'CUSTOMER_RATING',
  'best sellers': 'BEST_SELLERS',
  'price: low to high': 'PRICE_LOW_TO_HIGH',
  'price: high to low': 'PRICE_HIGH_TO_LOW',
  'brand name': 'BRAND_SORT'
};
export const evSortSearchClick = ({ sortType }) => ({
  sortSearchClick: {
    sortType: SortTypeMap[sortType.toLowerCase().trim()] || 'UNKNOWN_SORT'
  }
});

/**
  * https://code.amazon.com/packages/AmethystEvents/blobs/mainline/--/configuration/include/com/zappos/amethyst/website/SearchPageClickthrough.proto
  *
  * @param {integer} pageNumber - Page number, ex: 1
  * @param {integer} pageResultNumber - Position out of all results on page
  * @param {string} productIdentifiers - Keys: productId, styleId, colorId, stockId, asin
*/
export const evSearchPageClickthrough = ({ pageNumber, pageResultNumber, product, numberOfHearts, swatchCount, swatchIndex }) => {
  const { productId, styleId, colorId } = product;
  return {
    searchPageClickthrough: {
      pageNumber,
      pageResultNumber,
      clickedProduct: {
        productId,
        styleId,
        colorId,
        supplementalData: {
          numberOfHearts,
          swatchCount,
          swatchIndex
        }
      }
    }
  };
};

const tePageView = appState => {
  const { filters: { term, bestForYouSortEligible, personalizedSize } } = appState;
  trackEvent('TE_PV_SEARCHPAGE');
  if (personalizedSize) {
    trackEvent('TE_PERSONALIZED_SIZE_AVAILABLE');
  }

  if (bestForYouSortEligible) {
    trackEvent('TE_PERSONALIZED_SEARCH_BFU_ELIGIBLE');
    if (!term) {
      trackEvent('TE_PERSONALIZED_SEARCH_BFU_ELIGIBLE_NO_TERM');
    }
  }
};

const monetateSearchView = appState => {
  const { products } = appState;
  if (!products.isLoading && products?.list?.length > 0) {
    const viewedProducts = products.list.map(p => p.styleId);
    sendMonetateEvent(
      ['setPageType','search'],
      ['addProducts', viewedProducts]
    );
  } else {
    sendMonetateEvent(
      ['setPageType','noSearchResults']
    );
  }
};

export const evSponsoredProductClick = ({ styleId, productIndex }) => ({
  sponsoredProductClick: {
    pageType: 'SEARCH_PAGE',
    productIndex,
    productIdentifier: {
      styleId: Number(styleId)
    }
    // camapignId,
    // bidderName // Currently, this info isn't available in CC call, but it's on the Amethyst proto, so add when available
  }
});

export const evSponsoredProductView = ({ sponsoredProducts }) => {
  const numberOfSponsoredProducts = sponsoredProducts?.styles?.length;

  const impressions = sponsoredProducts?.styles.map((product, index) => ({
    numberOfSponsoredProducts,
    productIndex: index,
    productIdentifier: {
      styleId: Number(product.styleId)
    },
    pageType: 'SEARCH_PAGE'
    // camapignId,
    // bidderName // Currently, this info isn't available in CC call, but it's on the Amethyst proto, so add when available
  }));

  return ({
    sponsoredProductImpressionWrapper: {
      sponsoredProductImpression: impressions
    }
  });
};

export default {
  pageEvent: SEARCH_PAGE_VIEW,
  events: {
    [SEARCH_PAGE_VIEW]: [pvSearch, tePageView, monetateSearchView]
  }
};
